import React from "react";
import { Formik, Form } from "formik";
import styled from "styled-components";
import { getHeading3BoldTypography } from "../../../common/Typography";
import Button from "../../../common/components/Buttons/Button";
import { Paragraph16Regular } from "../../../common/Typography/Paragraph16";
import { useTranslation } from "react-i18next";
import { getHorizontalGap } from "../../../common/styles";
import ButtonOutlined from "../../../common/components/Buttons/ButtonOutlined";
import { ISignupArtist } from "../../../common/commonTypes";
import { useRouter } from "next/router";
import AlertMessage from "../../../common/components/AlertMessage";
import { convertProfileValidationSchema } from "./ConvertProfileSchema";
import ArtistTermsAndConditions from "../../../common/components/ArtistTermsAndConditions/ArtistTermsAndConditions";
import { useConvertProfileMutation } from "../../../queries/authentication.query";

const Root = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 108px;
  padding-bottom: 180px;
  margin: 0 auto;
`;

const ErrorWrapper = styled.div`
  margin: 16px 0;
`;

const FormErrorWrapper = styled.div`
  margin-top: 16px;
`;

const Title = styled.h3`
  color: ${(props) => props.theme.primaryColor};
  ${getHeading3BoldTypography};
  margin-bottom: 16px;
`;

const Description = styled(Paragraph16Regular)`
  opacity: 0.7;
  line-height: 20px;
`;

const TermsAndConditions = styled.div`
  margin: 40px 0;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: center;
  ${getHorizontalGap("16px")};
`;

const initialValues: Partial<ISignupArtist> = {
  confirm_no_copyright: false,
  confirm_ownership: false,
  confirm_listening_rights: false,
};

const ConvertProfile = () => {
  const { t } = useTranslation();
  const router = useRouter();
  const { mutate, isError, reset } = useConvertProfileMutation();

  return (
    <Root className="main-container">
      {isError && (
        <ErrorWrapper>
          <AlertMessage
            isOpen
            message={t("convert-profile-error")}
            onClose={reset}
          />
        </ErrorWrapper>
      )}

      <Formik
        initialValues={initialValues}
        onSubmit={() => mutate()}
        validationSchema={convertProfileValidationSchema}
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ errors, touched, isSubmitting }) => {
          const isFormError =
            (errors.confirm_no_copyright && touched.confirm_no_copyright) ||
            (errors.confirm_ownership && touched.confirm_ownership) ||
            (errors.confirm_listening_rights &&
              touched.confirm_listening_rights);

          return (
            <Form noValidate>
              <Title>{t("convert-profile")}</Title>
              <Description>{t("convert-profile-desc")}</Description>
              {isFormError && (
                <FormErrorWrapper>
                  <AlertMessage
                    isOpen
                    message={t("signup-details-accordion-form-error-checkbox")}
                  />
                </FormErrorWrapper>
              )}
              <TermsAndConditions>
                <ArtistTermsAndConditions openOnMount />
              </TermsAndConditions>
              <Buttons>
                <ButtonOutlined
                  type="button"
                  onClick={() => router.back()}
                  text={t("cancel")}
                  isLoading={isSubmitting}
                />
                <Button
                  type="submit"
                  text={t("confirm")}
                  isLoading={isSubmitting}
                />
              </Buttons>
            </Form>
          );
        }}
      </Formik>
    </Root>
  );
};

export default ConvertProfile;
